import {
	AfterViewChecked,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	TrackByFunction,
	ViewChild,
} from '@angular/core';
import { GoogleMap, MapInfoWindow, MapMarker, GoogleMapsModule } from '@angular/google-maps';
import { Loader } from '@googlemaps/js-api-loader';
import { checkIfStoreOpened, getBoundsZoomLevel, getStreet, getSVGIcon, mapState } from '../../helpers';
import { MapPinType, Site, SiteDetail } from '../../models';
import { StorePartialMapMarker } from '../../models/storePartialMapMaker';
import { NgIf, NgFor } from '@angular/common';

@Component({
	// tslint:disable-next-line:component-selector
	selector: 'store-locator-google-stores-map',
	template: `
		<div *ngIf="googleMapsInit">
			<google-map
				height="613px"
				width="100%"
				[zoom]="zoom"
				[center]="mapCenter"
				[options]="mapOptions"
				(mapDragend)="mapDragend()"
				(zoomChanged)="mapZoomChanged()"
			>
				<map-marker
					#markerElem
					*ngFor="let markerItem of markers; trackBy: trackByFn"
					[position]="markerItem.position"
					[label]="markerItem.label"
					[title]="markerItem.title"
					[options]="markerItem.options"
					(mapClick)="openInfo(markerItem, markerItem.info)"
				>
				</map-marker>
				<map-info-window
					#mapInfoWindow
					[position]="infoContentPosition"
					[options]="infoContentOptions"
					*ngIf="currentStore"
				>
					<article>
						<header class="infoHeader">
							<h3 (click)="storeSelected.emit(currentStore)" class="infoHeader-title">
								{{ currentStore.site?.name }}
							</h3>
						</header>
						<div class="infoSummary">
							<address>
								{{ street }} <br />
								{{ currentStore?.site?.suburb }} <br />
								{{ state }} {{ currentStore.site?.postcode }}
							</address>
							<p class="infoSummary-distance">{{ distance }}</p>
						</div>
					</article>
				</map-info-window>
			</google-map>
		</div>
	`,
	styleUrls: ['./google-stores-map.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, GoogleMapsModule, NgFor],
})
export class GoogleStoresMapComponent implements OnInit, AfterViewChecked {
	// TODO: once it's fully done clean up the unused map in styleguide https://woolworths-agile.atlassian.net/browse/CO-8119

	@ViewChild(GoogleMap, { static: false }) map: GoogleMap;

	@ViewChild(MapInfoWindow, { static: false }) info: MapInfoWindow;

	@ViewChild(MapMarker, { static: false }) public mapMarker: MapMarker;

	@Output() storeSelected = new EventEmitter<SiteDetail>();

	@Output() centreUpdated = new EventEmitter<{ lng: number; lat: number }>();

	@Output() zoomUpdated = new EventEmitter<number | undefined>();

	@Input() apiKey = '';

	_stores: SiteDetail[] = [];
	zoom: number;
	currentStore?: SiteDetail;
	street = '';
	state = '';
	distance = '';

	mapCenter: google.maps.LatLngLiteral;

	mapOptions: google.maps.MapOptions = {
		zoomControl: false,
		scrollwheel: false,
		disableDoubleClickZoom: true,
		mapTypeId: 'hybrid',
		maxZoom: 20,
		minZoom: 0,
	};

	markers: (StorePartialMapMarker | MapMarker)[] = [];

	infoContentPosition: google.maps.LatLngLiteral;

	infoContentOptions: google.maps.InfoWindowOptions;

	googleMapsInit = false;
	offset = { x: 0, y: -48 };
	// MapSizes are needed for the zoom calculation with the bounds.
	mapSizes = { width: 500, height: 500 };
	defaultZoom = 16;
	mapPositionSet = false;

	private ignoreZoomEvents = false;

	constructor(private cdr: ChangeDetectorRef) {}
	@Input()
	set stores(stores: SiteDetail[]) {
		this._stores = stores || [];

		// there's a race condition could be possible that the map hasn't initialised yet
		if (this.googleMapsInit) {
			this.updateMapPosition();
		}
	}

	@Input()
	set selectedStore(store: SiteDetail) {
		if (store?.site?.name) {
			this.handleSelectedLocation(store);
		}
	}

	trackByFn: TrackByFunction<StorePartialMapMarker> = (_, marker: StorePartialMapMarker) => marker.label?.text;

	ngOnInit(): void {
		const loader = new Loader({
			apiKey: this.apiKey,
			version: 'weekly',
		});

		loader.load().then(() => {
			this.googleMapsInit = true;
			this.mapOptions = this.setMapOptions();

			this.updateMapPosition();
			this.cdr.markForCheck();
		});
	}

	ngAfterViewChecked(): void {
		// Reset this after change detection since we only want to ignore the changes
		// fired by the map when we change the zoom in `updateMapPosition`
		this.ignoreZoomEvents = false;
	}

	setMapOptions(): google.maps.MapOptions {
		const options: google.maps.MapOptions = {} as google.maps.MapOptions;
		options.mapTypeId = 'roadmap';
		options.maxZoom = 20;
		options.minZoom = 5;

		return options;
	}

	getMapCenter(store?: Site): google.maps.LatLngLiteral {
		const center: google.maps.LatLngLiteral = {} as google.maps.LatLngLiteral;

		center.lat = Number(store?.latitude);
		center.lng = Number(store?.longitude);

		return center;
	}

	createBounds(markers: Array<StorePartialMapMarker | MapMarker>): google.maps.LatLngBounds {
		const bounds = new google.maps.LatLngBounds();

		markers.forEach((marker) => {
			const mapMarker = marker as MapMarker;
			bounds.extend(mapMarker.position);
		});

		return bounds;
	}

	getCenterBasedOnMarkers(bounds: google.maps.LatLngBounds): google.maps.LatLngLiteral {
		const centerPoint = bounds.getCenter();

		return { lat: centerPoint.lat(), lng: centerPoint.lng() };
	}

	getMarkers(stores: SiteDetail[]): Array<StorePartialMapMarker | MapMarker> {
		if (stores.length === 1) {
			return [this.createMarker(stores[0])];
		}

		return stores.map((s, i) => this.createMarkerWithDetails(s, i + 1));
	}
	createMarker(details: SiteDetail): {
		name: string | null | undefined;
		position: {
			lat: number;
			lng: number;
		};
		options: {
			icon: string;
		};
		getAnchor: () => void;
	} {
		return {
			name: details.site?.name,
			position: {
				lat: Number(details.site?.latitude),
				lng: Number(details.site?.longitude),
			},
			options: {
				icon: getSVGIcon(MapPinType.detail),
			},
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			getAnchor: () => {},
		};
	}

	createMarkerWithDetails(details: SiteDetail, displayNumber: number): StorePartialMapMarker {
		const pinType = checkIfStoreOpened(details) ? MapPinType.opened : MapPinType.closed;
		const pinIcon = getSVGIcon(pinType);

		return {
			position: {
				lat: Number(details.site?.latitude),
				lng: Number(details.site?.longitude),
			},
			info: details,
			options: {
				icon: {
					url: pinIcon,
					labelOrigin: {
						// The pin is 34 * 47px so label should be centered horizontally and near the top
						x: 17,
						y: 20,
					},
				},
			},
			label: {
				text: `${displayNumber}`,
				color: 'white',
				fontSize: '16px',
				fontWeight: 'bold',
			},
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			getAnchor: () => {},
		};
	}

	handleSelectedLocation(store: SiteDetail): void {
		if (this.currentStore?.site?.name === store?.site?.name || !this.markers.length) {
			return;
		}

		const results = this.markers.filter((m: any) => m.info?.site?.name === store?.site?.name);
		if (results.length) {
			this.currentStore = store;
			this.street = getStreet(store.site);
			this.state = mapState(store.site);
			this.distance = `${store?.distanceInKms} km`;

			this.openInfo(results[0] as MapMarker, store);
		}
	}

	openInfo(marker: MapMarker, storePin: SiteDetail): void {
		if (storePin && storePin.site) {
			this.selectedStore = storePin;

			this.infoContentPosition = {
				lat: Number(storePin.site.latitude),
				lng: Number(storePin.site.longitude),
			} as google.maps.LatLngLiteral;
		}

		this.infoContentOptions = {
			// to make the info appearing above the pin
			pixelOffset: new google.maps.Size(this.offset.x, this.offset.y),
		};

		if (this.info) {
			this.info.open(marker);
		}
	}

	mapDragend(): void {
		const center = this.map.getCenter();
		if (center) {
			this.centreUpdated.next({ lat: center.lat(), lng: center.lng() });
		}
	}

	mapZoomChanged(): void {
		if (this.ignoreZoomEvents) {
			return;
		}
		this.zoomUpdated.next(this.map.getZoom());
	}

	private updateMapPosition(): void {
		if (!this._stores?.length) {
			return;
		}

		this.markers = this.getMarkers(this._stores);

		if (this.mapPositionSet) {
			return;
		}

		this.ignoreZoomEvents = true;

		if (this._stores.length === 1) {
			this.mapCenter = this.getMapCenter(this._stores[0].site);
			this.zoom = this.defaultZoom;
		} else {
			const mapBounds = this.createBounds(this.markers);
			this.mapCenter = this.getCenterBasedOnMarkers(mapBounds);
			this.zoom = getBoundsZoomLevel(mapBounds, this.mapSizes);
		}

		// stops it from re-centring when dragging map around
		this.mapPositionSet = true;
	}
}
